/* eslint-disable @typescript-eslint/prefer-optional-chain */
/* eslint-disable no-param-reassign */
import { Injectable } from '@angular/core';

import { mapProductToProductUid } from '../related-products/application/transformers/products-to-product-uids.transformer';

import { isPolicyAllowed } from '@core/application/use-cases/user-consent/check-policy-allowed.use-case';
import { userConsentPolicies } from '@core/domain/constants/user-consent.const';
import { CoreOneTrustService } from '@core/infrastructure/one-trust/one-trust.service';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { ProductPublicModels } from '@features/products/product';
import { assertType } from '@frontend/common/utils';

@Injectable({
    providedIn: 'root',
})
export class ProductViewedService {
    private readonly daysOfExpiration = 30;

    private readonly localStorageKey = 'RECENTLY_VIEWED';

    constructor(
        private readonly platformService: CorePlatformService,
        private readonly storageService: CoreStorageService,
        private readonly oneTrustService: CoreOneTrustService
    ) { }

    public getStateRecentlyViewed(): { storeId: string; products: [] } {
        if (isPolicyAllowed(this.platformService, this.oneTrustService, userConsentPolicies.FUNCTIONAL_PERSONALIZATION)) {
            const state = this.storageService.getValue<{ storeId: string; products: [] }>(this.localStorageKey);

            if (assertType<{ storeId: string; products: [] }>(state)) {
                return state;
            }
        }
    }

    public setProductAsViewed(product: ProductPublicModels.PublicProduct, storeId: string) {
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        if (!product || !product.id || product.gridElemType === 'BUNDLE') {
            return;
        }

        const oldState = this.getStateRecentlyViewed();

        if (typeof oldState !== 'string' && oldState?.storeId !== storeId) {
            this.saveState({ storeId, products: [] }, product);
        } else {
            this.saveState(this.cleanExpiredProducts(oldState), product);
        }
    }

    private cleanExpiredProducts(state) {
        const numDaysBetween = (d1, d2) => {
            const diff = Math.abs(d1 - d2);
            return diff / (1000 * 60 * 60 * 24);
        };
        state.products = state.products.filter((p) => {
            return numDaysBetween(p.timestamp, new Date().getTime()) < this.daysOfExpiration;
        });
        return state;
    }

    // Duplicada en related-product.service
    private getProductColorId(product) {
        if (product?.uid) {
            return product.uid.slice(-3);
        }
        if (product?.status?.selectedColor) {
            return product.status.selectedColor.id;
        }
        return undefined;
    }

    private cleanIdDuplicated(arrayProducts, product) {
        return arrayProducts.filter((p) => p.product_id !== product.id);
    }

    private setStateRecentlyViewed(newState): boolean {
        return this.storageService.setValue(this.localStorageKey, newState, 'PERMANENT', 'FUNCTIONAL_PERSONALIZATION');
    }

    private saveState(state, product) {
        state.products = this.cleanIdDuplicated(state.products, product)
            .slice(-10).concat(mapProductToProductUid(product));
        this.setStateRecentlyViewed(state);
        // this.storageService.setValue(k, v, 'PERMANENT', 'FUNCTIONAL_PERSONALIZATION');
    }
}
